// @ts-nocheck
/**
 * Users Management Page (Admin Only)
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPlus, Users as UsersIcon, Shield, Eye, Pencil, Trash2, KeyRound } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { z } from "zod";
import { UserRole, useSession } from "@/state/session";
import { UserEditDialog } from "@/components/users/UserEditDialog";
import { UserDeleteDialog } from "@/components/users/UserDeleteDialog";
import { UserViewDialog } from "@/components/users/UserViewDialog";
import { InvitesTab } from "@/components/users/InvitesTab";

const createUserSchema = z.object({
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  email: z.string().email("E-mail inválido"),
  password: z.string().min(6, "Senha deve ter no mínimo 6 caracteres"),
  role: z.enum(["admin", "gestor", "funcionario"]),
});

interface UserWithRole {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  created_at: string;
}

/**
 * Diagnóstico detalhado de erros de Edge Functions
 */
function diagnoseEdgeFunctionError(error: any, functionName: string): {
  technicalLog: string;
  userMessage: string;
  severity: 'info' | 'warning' | 'error';
} {
  console.group(`🔍 [Edge Function Error] ${functionName}`);
  console.error('Raw error:', error);
  console.error('Error type:', typeof error);
  console.error('Error keys:', error ? Object.keys(error) : 'null');
  
  let technicalLog = '';
  let userMessage = '';
  let severity: 'info' | 'warning' | 'error' = 'error';

  // Analisar diferentes tipos de erro
  if (!error) {
    technicalLog = 'Erro desconhecido (null/undefined)';
    userMessage = 'Ocorreu um erro inesperado. Tente novamente.';
  } else if (error.message?.includes('Failed to fetch') || error.message?.includes('fetch')) {
    technicalLog = `Network error ao chamar ${functionName}: ${error.message}`;
    userMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
    console.error('⚠️ Possíveis causas:');
    console.error('  1. Edge function não está implantada');
    console.error('  2. Nome da função está incorreto');
    console.error('  3. Problemas de rede');
    console.error('  4. CORS não configurado');
  } else if (error.status === 404) {
    technicalLog = `Edge function "${functionName}" não encontrada (404)`;
    userMessage = 'Funcionalidade temporariamente indisponível.';
    console.error('⚠️ A função não existe ou não foi implantada');
    severity = 'warning';
  } else if (error.status === 401) {
    technicalLog = `Não autorizado ao chamar ${functionName} (401)`;
    userMessage = 'Sua sessão expirou. Faça login novamente.';
    console.error('⚠️ Token de autenticação inválido ou expirado');
  } else if (error.status === 403) {
    technicalLog = `Permissão negada para ${functionName} (403)`;
    userMessage = 'Você não tem permissão para acessar esses dados.';
    console.error('⚠️ Usuário não tem role "admin" ou JWT não verificado');
  } else if (error.status === 500) {
    technicalLog = `Erro interno no servidor ao chamar ${functionName} (500)`;
    userMessage = 'Erro no servidor. Nossa equipe foi notificada.';
    console.error('⚠️ Verifique os logs da edge function no Supabase');
  } else {
    technicalLog = `Erro desconhecido ao chamar ${functionName}: ${JSON.stringify(error)}`;
    userMessage = 'Não foi possível concluir a operação. Tente novamente.';
  }

  console.error('📝 Log técnico:', technicalLog);
  console.error('💬 Mensagem ao usuário:', userMessage);
  console.groupEnd();

  return { technicalLog, userMessage, severity };
}

export default function Users() {
  const { toast } = useToast();
  const { user: currentUser } = useSession();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<UserRole>("funcionario");
  const [isCreating, setIsCreating] = useState(false);

  // Dialog states for actions
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const roleLabels: Record<UserRole, string> = {
    admin: "Administrador",
    gestor: "Gestor",
    funcionario: "Funcionário",
  };

  const roleBadgeVariants: Record<UserRole, "default" | "secondary" | "destructive"> = {
    admin: "destructive",
    gestor: "default",
    funcionario: "secondary",
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    console.log('🚀 [loadUsers] Iniciando carregamento de usuários...');
    
    try {
      // 1. Buscar profiles
      console.log('📋 [loadUsers] Buscando profiles...');
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, name, created_at");

      if (profilesError) {
        console.error('❌ [loadUsers] Erro ao buscar profiles:', profilesError);
        throw profilesError;
      }
      console.log(`✅ [loadUsers] ${profiles?.length || 0} profiles carregados`);

      // 2. Buscar roles
      console.log('👤 [loadUsers] Buscando roles...');
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) {
        console.error('❌ [loadUsers] Erro ao buscar roles:', rolesError);
        throw rolesError;
      }
      console.log(`✅ [loadUsers] ${roles?.length || 0} roles carregados`);

      // 3. Buscar emails via edge function (com tratamento especial)
      console.log('📧 [loadUsers] Tentando buscar emails via edge function...');
      console.time('get-user-emails');
      
      const { data: emailsData, error: emailsError } = await supabase.functions.invoke('get-user-emails');
      
      console.timeEnd('get-user-emails');
      
      const emailsMap = new Map<string, string>();
      
      if (emailsError) {
        // Diagnosticar o erro detalhadamente
        const diagnosis = diagnoseEdgeFunctionError(emailsError, 'get-user-emails');
        
        // Toast amigável para o usuário
        toast({
          title: diagnosis.severity === 'warning' ? "⚠️ Aviso" : "ℹ️ Informação",
          description: diagnosis.userMessage + " A lista de usuários será exibida sem os emails.",
          variant: diagnosis.severity === 'error' ? "destructive" : "default",
          duration: 5000,
        });
        
        console.warn('⚠️ [loadUsers] Continuando sem emails...');
      } else if (emailsData?.users) {
        console.log(`✅ [loadUsers] ${emailsData.users.length} emails carregados`);
        emailsData.users.forEach((u: { id: string; email: string }) => {
          emailsMap.set(u.id, u.email);
        });
      } else {
        console.warn('⚠️ [loadUsers] Resposta da edge function sem dados de usuários');
      }

      // 4. Combinar dados
      console.log('🔄 [loadUsers] Combinando dados...');
      const usersData: UserWithRole[] = profiles.map((profile) => {
        const userRole = roles.find(r => r.user_id === profile.id);
        const email = emailsMap.get(profile.id);
        
        if (!email) {
          console.warn(`⚠️ [loadUsers] Email não encontrado para usuário: ${profile.name} (${profile.id})`);
        }
        
        return {
          id: profile.id,
          name: profile.name,
          email: email || "(email não disponível)",
          role: userRole?.role as UserRole || "funcionario",
          created_at: profile.created_at,
        };
      });

      console.log(`✅ [loadUsers] ${usersData.length} usuários processados com sucesso`);
      setUsers(usersData);
      
    } catch (error: any) {
      console.error('❌ [loadUsers] Erro crítico:', error);
      
      const diagnosis = diagnoseEdgeFunctionError(error, 'loadUsers');
      
      toast({
        title: "❌ Erro ao Carregar Usuários",
        description: diagnosis.userMessage,
        variant: "destructive",
        duration: 7000,
      });
    } finally {
      setIsLoading(false);
      console.log('🏁 [loadUsers] Finalizado');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🆕 [createUser] Iniciando criação de usuário...');
    setIsCreating(true);

    try {
      // Validação
      console.log('✔️ [createUser] Validando dados...');
      createUserSchema.parse({
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole,
      });
      console.log('✅ [createUser] Dados validados');

      // Chamar edge function
      console.log('📡 [createUser] Chamando edge function create-user...');
      console.time('create-user');
      
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          name: newUserName,
          email: newUserEmail,
          password: newUserPassword,
          role: newUserRole,
        },
      });
      
      console.timeEnd('create-user');

      if (error) {
        const diagnosis = diagnoseEdgeFunctionError(error, 'create-user');
        
        toast({
          title: "❌ Erro ao Criar Usuário",
          description: diagnosis.userMessage,
          variant: "destructive",
          duration: 7000,
        });
        
        throw error;
      }

      console.log('✅ [createUser] Usuário criado com sucesso:', data);

      toast({
        title: "✅ Usuário Criado!",
        description: `${newUserName} foi adicionado ao sistema como ${roleLabels[newUserRole]}.`,
        duration: 4000,
      });

      // Limpar formulário
      setNewUserName("");
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserRole("funcionario");
      setShowCreateDialog(false);
      
      // Recarregar lista
      console.log('🔄 [createUser] Recarregando lista de usuários...');
      loadUsers();
      
    } catch (error: any) {
      console.error('❌ [createUser] Erro:', error);
      
      // Erros de validação Zod
      if (error?.issues) {
        const validationErrors = error.issues.map((issue: any) => issue.message).join(', ');
        console.error('⚠️ [createUser] Erros de validação:', validationErrors);
        
        toast({
          title: "⚠️ Dados Inválidos",
          description: validationErrors,
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setIsCreating(false);
      console.log('🏁 [createUser] Finalizado');
    }
  };

  const handleResetPassword = async (email: string) => {
    if (email === "(email não disponível)") {
      toast({
        title: "⚠️ Email Indisponível",
        description: "Não é possível enviar link de reset para este usuário.",
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    setIsResettingPassword(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "📧 Email Enviado!",
        description: `Link de recuperação de senha enviado para ${email}`,
        duration: 5000,
      });
    } catch (error: any) {
      console.error('❌ Erro ao enviar reset de senha:', error);
      toast({
        title: "❌ Erro ao Enviar Email",
        description: "Não foi possível enviar o email de recuperação. Tente novamente.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleView = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowViewDialog(true);
  };

  const handleEdit = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleDelete = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <UsersIcon className="w-8 h-8" />
            Gerenciar Usuários
          </h1>
          <p className="text-muted-foreground mt-1">
            Crie e gerencie usuários do sistema
          </p>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="users" className="gap-2">
              <UsersIcon className="h-4 w-4" />
              Usuários Ativos
            </TabsTrigger>
            <TabsTrigger value="invites" className="gap-2">
              <UserPlus className="h-4 w-4" />
              Convites
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-end">

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Criar Usuário
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Usuário</DialogTitle>
                <DialogDescription>
                  Adicione um novo usuário ao sistema e atribua sua função
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="Nome completo"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="usuario@pdz.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Senha Inicial</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="••••••••"
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    O usuário poderá alterar a senha após o primeiro login
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Função</Label>
                  <Select value={newUserRole} onValueChange={(value) => setNewUserRole(value as UserRole)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <span className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Administrador
                        </span>
                      </SelectItem>
                      <SelectItem value="gestor">Gestor</SelectItem>
                      <SelectItem value="funcionario">Funcionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Criar Usuário
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            </div>

            <Card>
          <CardHeader>
            <CardTitle>Usuários do Sistema</CardTitle>
            <CardDescription>
              Lista de todos os usuários cadastrados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" text="Carregando usuários..." />
              </div>
            ) : (
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Função</TableHead>
                      <TableHead>Data de Criação</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className={user.email === "(email não disponível)" ? "text-muted-foreground italic" : "text-muted-foreground"}>
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <Badge variant={roleBadgeVariants[user.role]}>
                            {roleLabels[user.role]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleView(user)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver detalhes</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleResetPassword(user.email)}
                                  disabled={user.email === "(email não disponível)" || isResettingPassword}
                                >
                                  <KeyRound className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Resetar senha</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(user)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar usuário</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(user)}
                                  disabled={user.id === currentUser?.id}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {user.id === currentUser?.id 
                                  ? "Não pode deletar você mesmo" 
                                  : "Deletar usuário"}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="invites">
            <InvitesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <UserViewDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        user={selectedUser}
      />

      <UserEditDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        user={selectedUser}
        onSuccess={loadUsers}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        user={selectedUser}
        onSuccess={loadUsers}
      />
    </Layout>
  );
}
